import React from 'react'

import { getFieldPlaceholder } from 'features/views/ListView/Actions/utils'

import { SectionAttachmentAttributeEditor } from './SectionAttachmentAttributeEditor'
import { SectionCheckboxAttributeEditor } from './SectionCheckboxAttributeEditor'
import { SectionCurrencyAttributeEditor } from './SectionCurrencyAttributeEditor'
import { SectionDateAttributeEditor } from './SectionDateAttributeEditor'
import { SectionDropdownAttributeEditor } from './SectionDropdownAttributeEditor'
import { SectionLongTextAttributeEditor } from './SectionLongTextAttributeEditor'
import { SectionNumberAttributeEditor } from './SectionNumberAttributeEditor'
import { SectionPercentageAttributeEditor } from './SectionPercentageAttributeEditor'
import { SectionRecordLinksAttributeEditor } from './SectionRecordLinksAttributeEditor'
import { SectionRichTextAttributeEditor } from './SectionRichTextAttributeEditor'
import { SectionTextAttributeEditor } from './SectionTextAttributeEditor'
import { SectionUserAttributeEditor } from './SectionUserAttributeEditor'
import { SectionAttributeEditorProps as EditorProps } from './types'

type AttributeValue<C extends React.ComponentType<any>> = React.ComponentProps<C>['value']

type SectionAttributeEditorProps = EditorProps<unknown> & {
    record: RecordDto
}

export const SectionAttributeEditor: React.FC<SectionAttributeEditorProps> = ({
    label,
    field,
    isReadOnly,
    ...props
}) => {
    const effectiveLabel = label || field.label
    const placeholder = getFieldPlaceholder(field, !isReadOnly)

    return (
        <SectionAttributeEditorContent
            field={field}
            {...props}
            label={effectiveLabel}
            placeholder={placeholder}
            isReadOnly={isReadOnly}
        />
    )
}

const SectionAttributeEditorContent: React.FC<SectionAttributeEditorProps> = React.memo(
    function SectionAttributeEditorContent({ field, value, record, ...props }) {
        switch (field.type) {
            case 'string':
            case 'url':
                return (
                    <SectionTextAttributeEditor
                        field={field}
                        value={value as AttributeValue<typeof SectionTextAttributeEditor>}
                        {...props}
                    />
                )

            case 'number':
                return (
                    <SectionNumberAttributeEditor
                        field={field}
                        value={value as AttributeValue<typeof SectionNumberAttributeEditor>}
                        {...props}
                    />
                )

            case 'currency':
                return (
                    <SectionCurrencyAttributeEditor
                        field={field}
                        value={value as AttributeValue<typeof SectionCurrencyAttributeEditor>}
                        {...props}
                    />
                )

            case 'percentage':
                return (
                    <SectionPercentageAttributeEditor
                        field={field}
                        value={value as AttributeValue<typeof SectionPercentageAttributeEditor>}
                        {...props}
                    />
                )

            case 'checkbox':
                return (
                    <SectionCheckboxAttributeEditor
                        field={field}
                        value={value as AttributeValue<typeof SectionCheckboxAttributeEditor>}
                        {...props}
                    />
                )

            case 'date':
            case 'datetime':
                return (
                    <SectionDateAttributeEditor
                        field={field}
                        value={value as AttributeValue<typeof SectionDateAttributeEditor>}
                        {...props}
                    />
                )

            case 'long_text':
                return (
                    <SectionLongTextAttributeEditor
                        field={field}
                        value={value as AttributeValue<typeof SectionLongTextAttributeEditor>}
                        {...props}
                    />
                )

            case 'document':
                return (
                    <SectionRichTextAttributeEditor
                        field={field}
                        value={value as AttributeValue<typeof SectionRichTextAttributeEditor>}
                        {...props}
                    />
                )

            case 'user_ref':
                return (
                    <SectionUserAttributeEditor
                        field={field}
                        value={value as AttributeValue<typeof SectionUserAttributeEditor>}
                        {...props}
                    />
                )

            case 'dropdown':
            case 'multi_select':
                return (
                    <SectionDropdownAttributeEditor
                        field={field}
                        value={value as AttributeValue<typeof SectionDropdownAttributeEditor>}
                        {...props}
                    />
                )

            case 'lookup':
            case 'multi_lookup':
                return (
                    <SectionRecordLinksAttributeEditor
                        field={field}
                        value={value as AttributeValue<typeof SectionRecordLinksAttributeEditor>}
                        record={record}
                        {...props}
                    />
                )

            case 'multi_file':
                return (
                    <SectionAttachmentAttributeEditor
                        field={field}
                        value={value as AttributeValue<typeof SectionAttachmentAttributeEditor>}
                        {...props}
                    />
                )

            default:
                return null
        }
    }
)
